/**
 * Notification Rate Limiter
 *
 * Prevents notification flooding by limiting the number of notifications
 * per minute, both globally and per category.
 *
 * Priority levels affect limits:
 * - urgent: Always allowed (bypass limits)
 * - high: Allowed with small burst allowance
 * - normal/low: Subject to full rate limits
 */

import logger from '@/lib/logger';
import {
  NotificationPriority,
  RateLimitConfig,
  RateLimitResult,
  DEFAULT_RATE_LIMITS,
} from './types';
import { NotificationCategory } from '@/store/slices/notificationsTypes';

interface QueuedNotification {
  category: NotificationCategory;
  priority: NotificationPriority;
  timestamp: number;
  callback: () => void;
}

/**
 * Rate limiter for notifications using sliding window algorithm.
 */
class NotificationRateLimiter {
  private timestamps: Map<string, number[]> = new Map(); // category -> timestamps
  private globalTimestamps: number[] = [];
  private queue: QueuedNotification[] = [];
  private config: RateLimitConfig;
  private processingInterval: ReturnType<typeof setInterval> | null = null;

  constructor(config: RateLimitConfig = DEFAULT_RATE_LIMITS) {
    this.config = config;

    // Process queued notifications every second
    if (typeof window !== 'undefined') {
      this.processingInterval = setInterval(() => this.processQueue(), 1000);
    }
  }

  /**
   * Check if a notification can be shown.
   *
   * @param category - The notification category
   * @param priority - The notification priority
   * @returns RateLimitResult indicating if allowed
   */
  canShow(category: NotificationCategory, priority: NotificationPriority): RateLimitResult {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Clean old entries
    this.cleanOldTimestamps(oneMinuteAgo);

    // Get current counts
    const categoryTimestamps = this.timestamps.get(category) || [];
    const categoryCount = categoryTimestamps.length;
    const globalCount = this.globalTimestamps.length;

    // Urgent always allowed
    if (priority === 'urgent') {
      return { allowed: true };
    }

    // High priority gets burst allowance
    if (priority === 'high') {
      const highLimit = this.config.maxPerMinute + this.config.burstAllowance;
      if (globalCount < highLimit) {
        return { allowed: true };
      }
      return {
        allowed: false,
        queuePosition: globalCount - highLimit + 1,
        reason: 'Global rate limit exceeded',
      };
    }

    // Check global limit
    if (globalCount >= this.config.maxPerMinute) {
      return {
        allowed: false,
        queuePosition: globalCount - this.config.maxPerMinute + 1,
        reason: 'Global rate limit exceeded',
      };
    }

    // Check category limit
    if (categoryCount >= this.config.maxPerCategory) {
      return {
        allowed: false,
        queuePosition: categoryCount - this.config.maxPerCategory + 1,
        reason: `Category '${category}' rate limit exceeded`,
      };
    }

    return { allowed: true };
  }

  /**
   * Record a notification that was shown.
   */
  record(category: NotificationCategory): void {
    const now = Date.now();

    // Record in category
    const categoryTimestamps = this.timestamps.get(category) || [];
    categoryTimestamps.push(now);
    this.timestamps.set(category, categoryTimestamps);

    // Record globally
    this.globalTimestamps.push(now);
  }

  /**
   * Add a notification to the queue for later processing.
   */
  enqueue(
    category: NotificationCategory,
    priority: NotificationPriority,
    callback: () => void
  ): void {
    this.queue.push({
      category,
      priority,
      timestamp: Date.now(),
      callback,
    });

    // Sort by priority (higher priority first) then by timestamp
    this.queue.sort((a, b) => {
      const priorityOrder: Record<NotificationPriority, number> = {
        urgent: 0,
        high: 1,
        normal: 2,
        low: 3,
      };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return a.timestamp - b.timestamp;
    });
  }

  /**
   * Process queued notifications.
   */
  private processQueue(): void {
    if (this.queue.length === 0) return;

    const now = Date.now();
    const maxQueueAge = 300000; // 5 minutes max queue time

    // Remove stale items
    this.queue = this.queue.filter(item => now - item.timestamp < maxQueueAge);

    // Try to process items
    const toProcess: QueuedNotification[] = [];
    const toKeep: QueuedNotification[] = [];

    for (const item of this.queue) {
      const result = this.canShow(item.category, item.priority);
      if (result.allowed) {
        toProcess.push(item);
      } else {
        toKeep.push(item);
      }
    }

    // Process allowed items
    for (const item of toProcess) {
      this.record(item.category);
      try {
        item.callback();
      } catch (error) {
        logger.error('RateLimiter', 'Error processing queued notification', { error });
      }
    }

    this.queue = toKeep;
  }

  /**
   * Clean old timestamps outside the window.
   */
  private cleanOldTimestamps(threshold: number): void {
    // Clean global
    this.globalTimestamps = this.globalTimestamps.filter(t => t > threshold);

    // Clean per-category
    for (const [category, timestamps] of this.timestamps.entries()) {
      const filtered = timestamps.filter(t => t > threshold);
      if (filtered.length === 0) {
        this.timestamps.delete(category);
      } else {
        this.timestamps.set(category, filtered);
      }
    }
  }

  /**
   * Get current counts for debugging.
   */
  getStats(): { global: number; byCategory: Record<string, number> } {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    this.cleanOldTimestamps(oneMinuteAgo);

    const byCategory: Record<string, number> = {};
    for (const [category, timestamps] of this.timestamps.entries()) {
      byCategory[category] = timestamps.length;
    }

    return {
      global: this.globalTimestamps.length,
      byCategory,
    };
  }

  /**
   * Get queue length.
   */
  getQueueLength(): number {
    return this.queue.length;
  }

  /**
   * Update rate limit configuration.
   */
  updateConfig(config: Partial<RateLimitConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Clear all rate limit tracking.
   */
  clear(): void {
    this.timestamps.clear();
    this.globalTimestamps = [];
    this.queue = [];
  }

  /**
   * Destroy the rate limiter and clean up resources.
   */
  destroy(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }
    this.clear();
  }
}

// Singleton instance
let rateLimiterInstance: NotificationRateLimiter | null = null;

/**
 * Get the singleton rate limiter instance.
 */
export function getRateLimiter(): NotificationRateLimiter {
  if (!rateLimiterInstance) {
    rateLimiterInstance = new NotificationRateLimiter();
  }
  return rateLimiterInstance;
}

/**
 * Check if a notification can be shown.
 */
export function checkRateLimit(
  category: NotificationCategory,
  priority: NotificationPriority
): RateLimitResult {
  return getRateLimiter().canShow(category, priority);
}

/**
 * Record a notification that was shown.
 */
export function recordNotification(category: NotificationCategory): void {
  getRateLimiter().record(category);
}

/**
 * Enqueue a notification for later processing.
 */
export function enqueueNotification(
  category: NotificationCategory,
  priority: NotificationPriority,
  callback: () => void
): void {
  getRateLimiter().enqueue(category, priority, callback);
}

// Export the class for testing
export { NotificationRateLimiter };
